<template>
  <div class="triage-assessment">
    <form v-if="state === 'create'" @submit.prevent="submitAssessment">
      <div class="grid grid-cols-2 gap-x-20 gap-y-1">
        <!-- Reason for visit -->
        <div class="form-group">
          <label for="reasonForVisit" class="block text-sm font-medium text-gray-700">
            Reason for visit
          </label>
          <input
            type="text"
            id="reasonForVisit"
            v-model="assessment.reasonForVisit"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="Type..."
          />
        </div>

        <!-- Complaint/Symptoms -->
        <div class="form-group">
          <label for="complaints" class="block text-sm font-medium text-gray-700">
            Complaint/Symptoms
          </label>
          <input
            type="text"
            id="complaints"
            v-model="assessment.complaints"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="Type..."
          />
        </div>

        <!-- Duration Of Symptoms -->
        <div class="form-group">
          <label for="duration" class="block text-sm font-medium text-gray-700">
            Duration Of Symptoms
          </label>
          <input
            type="text"
            id="duration"
            v-model="assessment.duration"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="Type..."
          />
        </div>

        <!-- Pain Level -->
        <div class="form-group">
          <label for="painLevel" class="block text-sm font-medium text-gray-700">
            Pain Level
          </label>
          <input
            type="number"
            id="painLevel"
            v-model="assessment.painLevel"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="Scale of (1-10)"
            min="1"
            max="10"
          />
        </div>

        <!-- Risk of Fall -->
        <div class="form-group">
          <label for="riskOfFall" class="block text-sm font-medium text-gray-700">
            Risk of Fall
          </label>
          <input
            type="number"
            id="riskOfFall"
            v-model="assessment.riskOfFall"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="Scale (1-10)"
            min="1"
            max="10"
          />
        </div>

        <!-- Pregnant -->
        <div class="form-group" v-if="gender === 'Female'">
          <label for="pregnant" class="block text-sm font-medium text-gray-700">
            Pregnant
          </label>
          <select
            id="pregnant"
            v-model="assessment.pregnant"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
          >
            <option value="" disabled selected hidden>Select an option</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
            <option value="unknown">I don't know</option>
          </select>
        </div>

        <!-- Breast Feeding -->
        <div class="form-group" v-if="gender === 'Female'">
          <label for="breastFeeding" class="block text-sm font-medium text-gray-700">
            Breast Feeding
          </label>
          <select
            id="breastFeeding"
            v-model="assessment.breastFeeding"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
          >
            <option value="" disabled selected hidden>Select an option</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <!-- Is Terminal -->
        <div class="form-group">
          <label for="isTerminal" class="block text-sm font-medium text-gray-700">
            Is Terminal
          </label>
          <select
            id="isTerminal"
            v-model="assessment.isTerminal"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
          >
            <option value="" disabled selected hidden>Select an option</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      </div>

      <h3 class="text-lg font-medium text-gray-900 mt-2 mb-3">Glasgow Coma Scale</h3>

      <div class="grid grid-cols-2 gap-x-20 gap-y-1">
        <!-- Eye Response -->
        <div class="form-group">
          <label for="eyeResponse" class="block text-sm font-medium text-gray-700">
            Eye Response
          </label>
          <select
            id="eyeResponse"
            v-model="assessment.eyeResponse"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
          >
            <option value="" disabled selected hidden>Select an option between 1 to 4</option>
            <option value="1">1 - No eye opening</option>
            <option value="2">2 - Eye opening to pain</option>
            <option value="3">3 - Eye opening to verbal command</option>
            <option value="4">4 - Eyes open spontaneously</option>
          </select>
        </div>

        <!-- Motor Response -->
        <div class="form-group">
          <label for="motorResponse" class="block text-sm font-medium text-gray-700">
            Motor Response
          </label>
          <select
            id="motorResponse"
            v-model="assessment.motorResponse"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
          >
            <option value="" disabled selected hidden>Select an option between 1 to 6</option>
            <option value="1">1 - No motor response</option>
            <option value="2">2 - Extension to pain</option>
            <option value="3">3 - Flexion to pain</option>
            <option value="4">4 - Withdrawal from pain</option>
            <option value="5">5 - Localizing pain</option>
            <option value="6">6 - Obeys commands</option>
          </select>
        </div>

        <!-- Verbal Response -->
        <div class="form-group">
          <label for="verbalResponse" class="block text-sm font-medium text-gray-700">
            Verbal Response
          </label>
          <select
            id="verbalResponse"
            v-model="assessment.verbalResponse"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
          >
            <option value="" disabled selected hidden>Select an option between 1 to 5</option>
            <option value="1">1 - No verbal response</option>
            <option value="2">2 - Incomprehensible sounds</option>
            <option value="3">3 - Inappropriate words</option>
            <option value="4">4 - Confused</option>
            <option value="5">5 - Oriented</option>
          </select>
        </div>

        <!-- Patient Triage Code (Read-only) -->
        <div class="form-group">
          <label class="block text-sm font-medium text-gray-700">
            Patient Triage Code
            <span class="text-sm text-gray-500">(GCS Score: {{ gcsScore }})</span>
          </label>
          <select
            id="triageCode"
            v-model="assessment.triageCode"
            :class="['mt-1 block w-full rounded-md border shadow-sm cursor-not-allowed transition-colors duration-200', getTriageColor]"
            disabled
          >
            <option value="red">Red - Immediate</option>
            <option value="orange">Orange - Urgent</option>
            <option value="yellow">Yellow - Less Urgent</option>
            <option value="green">Green - Non-Urgent</option>
            <option value="blue">Blue - Non-Urgent</option>
          </select>
        </div>
      </div>

      <!-- ── Canadian Triage and Acuity Scale (CTAS) ── -->
      <h3 class="text-lg font-medium text-gray-900 mt-6 mb-3">
        Canadian Triage and Acuity Scale (CTAS)
      </h3>
      <div class="grid grid-cols-2 gap-x-20 gap-y-1">
        <div class="form-group col-span-2">
          <label for="ctasLevel" class="block text-sm font-medium text-gray-700">
            CTAS Level
            <span class="text-xs text-gray-400 ml-1">— Nurse's independent clinical judgment</span>
          </label>
          <select
            id="ctasLevel"
            v-model="assessment.ctasLevel"
            :class="['mt-1 block w-full rounded-md border shadow-sm focus:ring focus:ring-opacity-50 transition-colors duration-200', ctasColorClass]"
          >
            <option value="" disabled selected hidden>Select CTAS Level</option>
            <option value="1">Level I — Resuscitation (Immediate, life-threatening)</option>
            <option value="2">Level II — Emergent (≤ 15 min, high risk of deterioration)</option>
            <option value="3">Level III — Urgent (≤ 30 min, significant distress)</option>
            <option value="4">Level IV — Less Urgent (≤ 60 min, stable)</option>
            <option value="5">Level V — Non-Urgent (≤ 120 min, no acute distress)</option>
          </select>
        </div>
      </div>
    </form>

    <div v-if="state === 'view'">
      <div class="grid grid-cols-1 px-4 gap-4 mb-6">
        <div><p class="font-semibold">Reason for Visit:</p><p>A cut on the left arm.</p></div>
        <div>
          <p class="font-semibold">Triage Nurse Notes:</p>
          <p>The cut was not deep. The cut was dressed. Prescribed antibiotics to avoid infecting the wound.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ConsultationTriageAssessmentTab",
  props: {
    state: { type: String, required: true },
    visitId: { type: String, required: true },
    gender: { type: String, required: true },
  },
  data() {
    return {
      assessment: {
        reasonForVisit: "",
        complaints: "",
        duration: "",
        painLevel: null,
        riskOfFall: null,
        pregnant: "",
        breastFeeding: "",
        isTerminal: "",
        eyeResponse: "",
        motorResponse: "",
        verbalResponse: "",
        triageCode: "",
        ctasLevel: ""
      },
    };
  },
  computed: {
    gcsScore() {
      const eye = parseInt(this.assessment.eyeResponse) || 0;
      const motor = parseInt(this.assessment.motorResponse) || 0;
      const verbal = parseInt(this.assessment.verbalResponse) || 0;
      return eye + motor + verbal;
    },
    getTriageColor() {
      switch (this.assessment.triageCode) {
        case "red": return "bg-red-300 text-red-800 border-red-300";
        case "orange": return "bg-orange-300 text-orange-800 border-orange-300";
        case "yellow": return "bg-yellow-300 text-yellow-800 border-yellow-300";
        case "green": return "bg-green-300 text-green-800 border-green-300";
        case "blue": return "bg-blue-300 text-blue-800 border-blue-300";
        default: return "bg-gray-50 text-gray-800 border-gray-300";
      }
    },
    ctasColorClass() {
      switch (String(this.assessment.ctasLevel)) {
        case "1": return "bg-blue-600 text-white border-blue-600 focus:ring-blue-400";
        case "2": return "bg-red-500 text-white border-red-500 focus:ring-red-400";
        case "3": return "bg-yellow-400 text-yellow-900 border-yellow-400 focus:ring-yellow-300";
        case "4": return "bg-green-500 text-white border-green-500 focus:ring-green-400";
        case "5": return "bg-gray-200 text-gray-800 border-gray-300 focus:ring-gray-200";
        default:  return "bg-gray-50 text-gray-800 border-gray-300 focus:ring-custom-yellow";
      }
    },
  },
  watch: {
    gcsScore: {
      immediate: true,
      handler(score) {
        if (this.assessment.eyeResponse && this.assessment.motorResponse && this.assessment.verbalResponse) {
          if (score <= 8) this.assessment.triageCode = "red";
          else if (score <= 13) this.assessment.triageCode = "orange";
          else if (score <= 14) this.assessment.triageCode = "yellow";
          else this.assessment.triageCode = "green";
        }
      },
    },
  },
  methods: {
    submitAssessment() {
      this.$emit("assessment-submitted", this.assessment);
    },
  },
};
</script>

<style scoped>
.form-group { margin-bottom: 1rem; }
</style>